import React from 'react';
import {
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
  useOutlet,
  useSubmit
} from 'react-router-dom';
import * as Headless from '@headlessui/react';
import { motion, AnimatePresence } from 'motion/react';

import {
  PhoneIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  XMarkIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { PageTitle } from '@oysterjs/ui/page';
import { Await } from '@oysterjs/ui/async';
import { DataTable } from '@oysterjs/ui/data-table';
import { PageOutlet } from '@oysterjs/ui/navigation';
import { SearchFilterInput } from '@oysterjs/ui/search-filter';
import {
  Action,
  CallFieldsFragment,
  CallTranscriptFieldsFragment,
  useGetCallsLazyQuery,
  useSearchCallsQuery,
  Resource
} from '~/api/graphql';
import { usePermissions } from '~/components/permissions';
import { Spinner } from '@oysterjs/ui/spinner';
import { MountableSlideout } from '@oysterjs/ui/slideout';
import { Dialog, DialogTitle, DialogDescription, DialogActions } from '@oysterjs/ui/dialog';
import { Button } from '@oysterjs/ui/button';
import { downloadCsv as downloadCsvFile } from '~/lib/csv';
import { callsPage, usePageHeading } from '~/components/navigation';
import { useCallFilters } from '~/api/data/calls/calls-filters';
import {
  type CallSortKey,
  getCallColumns,
  callSortToCallSortInput
} from '~/components/calls/calls-columns';
import { ConnectedCallDetail } from '~/components/calls/call-detail-sections';
import { MarkCallSeenButton } from '~/components/calls/call-seen';

export const downloadCsv = (calls: CallFieldsFragment[]) => {
  const rows = [
    [
      'ID',
      'Customer',
      'Phone Number',
      'Status',
      'Sentiment',
      'End Reason',
      'Duration (s)',
      'Started At',
      'Ended At'
    ],
    ...calls.map((call) => [
      call.id,
      call.account?.account.name || call.fromName || '',
      call.fromNumber,
      call.status,
      call.sentiment || '',
      call.disconnectionReason || '',
      call.endedAt
        ? Math.round((new Date(call.endedAt).getTime() - new Date(call.startedAt).getTime()) / 1000)
        : '',
      new Date(call.startedAt).toISOString(),
      call.endedAt ? new Date(call.endedAt).toISOString() : ''
    ])
  ] as (string | number)[][];
  downloadCsvFile(rows, `calls-${new Date().toISOString().split('T')[0]}.csv`);
};

const UPDATE_CALL = [{ resource: Resource.Call, action: Action.Update }];

export const CallsPage = () => {
  const { title, description } = usePageHeading(callsPage);

  return (
    <PageTitle eyebrow={callsPage.name} title={title} description={description}>
      <PageOutlet />
    </PageTitle>
  );
};

const SEARCH_CALLS_PAGE_SIZE = 100;

export const CallsListPage = () => {
  const outlet = useOutlet();

  const [selectedCalls, setSelectedCalls] = React.useState<string[]>([]);
  const [showArchiveDialog, setShowArchiveDialog] = React.useState(false);
  // Archiving and marking seen are both call updates.
  const canUpdate = usePermissions().hasAllPermissions(UPDATE_CALL);
  const [sortKey, setSortKey] = React.useState<CallSortKey | undefined>('date');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('desc');
  const [exportError, setExportError] = React.useState<string | null>(null);
  const submit = useSubmit();
  const navigation = useNavigation();
  const [getCallsForExport, { loading: isExporting }] = useGetCallsLazyQuery();

  const isArchiving =
    navigation.state === 'submitting' && navigation.formAction?.endsWith('/archive');
  const isMarkingAsSeen =
    navigation.state === 'submitting' && navigation.formAction?.endsWith('/mark-seen');

  const selectedSet = React.useMemo(() => new Set(selectedCalls), [selectedCalls]);

  const { filterDefinitions, activeFilters, setActiveFilters, initialFilters, currentFilter } =
    useCallFilters();

  const sortInput = React.useMemo(
    () => callSortToCallSortInput(sortKey, sortDirection),
    [sortKey, sortDirection]
  );

  const {
    data: searchData,
    loading: searchLoading,
    error: searchError,
    fetchMore,
    refetch
  } = useSearchCallsQuery({
    variables: {
      first: SEARCH_CALLS_PAGE_SIZE,
      filter: currentFilter ?? undefined,
      sort: sortInput
    },
    notifyOnNetworkStatusChange: true
  });

  const connection = searchData?.searchCalls;
  const calls = React.useMemo(
    () => (connection?.edges ?? []).map((e) => e.node),
    [connection?.edges]
  );
  const hasNextPage = connection?.pageInfo.hasNextPage ?? false;
  const endCursor = connection?.pageInfo.endCursor ?? null;
  const totalCount = connection?.totalCount ?? 0;

  const isFetchingMoreRef = React.useRef(false);
  const loadMore = React.useCallback(() => {
    if (!endCursor || !hasNextPage || searchLoading || isFetchingMoreRef.current) return;
    isFetchingMoreRef.current = true;
    void fetchMore({
      variables: {
        after: endCursor,
        first: SEARCH_CALLS_PAGE_SIZE,
        filter: currentFilter ?? undefined,
        sort: sortInput
      }
    })
      .catch(() => undefined)
      .finally(() => {
        isFetchingMoreRef.current = false;
      });
  }, [endCursor, hasNextPage, searchLoading, fetchMore, currentFilter, sortInput]);

  const exportAllCalls = React.useCallback(async () => {
    setExportError(null);
    try {
      const result = await getCallsForExport({
        variables: { filter: currentFilter ?? undefined },
        fetchPolicy: 'network-only'
      });
      if (result.error || !result.data) {
        throw result.error ?? new Error('No call data returned');
      }
      downloadCsv(result.data.calls);
    } catch {
      setExportError('Unable to download calls. Please try again.');
    }
  }, [getCallsForExport, currentFilter]);

  React.useEffect(() => {
    setSelectedCalls([]);
  }, [currentFilter, sortKey, sortDirection]);

  const wasSubmittingRef = React.useRef(false);
  React.useEffect(() => {
    const submitting = isArchiving || isMarkingAsSeen;
    if (wasSubmittingRef.current && !submitting) {
      refetch();
    }
    wasSubmittingRef.current = submitting;
  }, [isArchiving, isMarkingAsSeen, refetch]);

  const columns = getCallColumns({
    selectedSet,
    setSelectedCalls,
    filtered: calls
  });

  return (
    <>
      <div className="mb-4 flex items-center gap-4">
        <div className="flex-1">
          <SearchFilterInput
            filterDefinitions={filterDefinitions}
            onFiltersChange={setActiveFilters}
            initialFilters={initialFilters}
            placeholder="Filter by voice agent, sentiment, transferred, phone, or date..."
          />
        </div>
        <div className="flex items-center gap-2">
          {exportError && (
            <span className="text-xs text-rose-600 dark:text-rose-400">{exportError}</span>
          )}
          <Button
            type="button"
            color="white"
            className="h-12"
            loading={isExporting}
            disabled={totalCount === 0}
            onClick={() => void exportAllCalls()}
          >
            <ArrowDownTrayIcon className="size-4" />
            Download CSV
          </Button>
        </div>
      </div>

      {searchLoading && calls.length === 0 ? (
        <div className="px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
          <div className="text-center py-12">
            <Spinner size="size-8" color="neutral" />
            <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
              Loading inbox...
            </h3>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              This might take a moment.
            </p>
          </div>
        </div>
      ) : searchError && calls.length === 0 ? (
        <div className="px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
          <div className="text-center py-12">
            <PhoneIcon className="mx-auto h-12 w-12 text-rose-400" />
            <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
              Unable to load calls
            </h3>
            <p className="mt-1 text-sm text-rose-600 dark:text-rose-400">{searchError.message}</p>
          </div>
        </div>
      ) : (
        <div className="relative px-4 sm:px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
          <AnimatePresence>
            {canUpdate && selectedCalls.length > 0 && (
              <motion.div
                className="fixed left-1/2 lg:left-[calc(8rem+50vw)] bottom-12 shadow-xl border border-neutral-100 dark:border-neutral-700 rounded-full bg-white dark:bg-neutral-800 p-2 z-10 flex gap-2"
                initial={{ opacity: 0, translateX: '-50%', translateY: 100, scale: 0.9 }}
                animate={{ opacity: 1, translateX: '-50%', translateY: 0, scale: 1 }}
                exit={{ opacity: 0, translateX: '-50%', translateY: 100, scale: 0.9 }}
                transition={{ duration: 0.15, ease: 'easeInOut' }}
              >
                <Headless.Button
                  className="flex items-center gap-x-1 rounded-full px-3 py-1.5 text-xs font-medium flex-shrink-0 text-white bg-blue-600/80 border border-transparent hover:bg-blue-600/90"
                  onClick={() => setSelectedCalls([])}
                >
                  <div>{selectedCalls.length} selected</div>
                  <XMarkIcon className="size-3.5" />
                </Headless.Button>
                <Headless.Button
                  className="flex items-center gap-x-1 rounded-full px-3 py-1.5 text-xs font-medium flex-shrink-0 text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 border-dashed hover:bg-neutral-100/50 dark:hover:bg-white/10 disabled:text-neutral-500 dark:disabled:text-neutral-400 disabled:bg-neutral-50/50 dark:disabled:bg-neutral-900/50 disabled:border-neutral-200 dark:disabled:border-neutral-700 disabled:cursor-default"
                  onClick={() =>
                    submit(
                      { callIds: selectedCalls },
                      {
                        method: 'POST',
                        action: '/app/calls/log/mark-seen',
                        encType: 'application/json'
                      }
                    )
                  }
                  disabled={isMarkingAsSeen}
                >
                  <EyeIcon className="size-3.5" />
                  Mark as Seen
                  {isMarkingAsSeen && <Spinner size="size-3.5" color="neutral" />}
                </Headless.Button>
                <Headless.Button
                  className="flex items-center gap-x-1 rounded-full px-3 py-1.5 text-xs font-medium flex-shrink-0 text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 border-dashed hover:bg-neutral-100/50 dark:hover:bg-white/10 disabled:text-neutral-500 dark:disabled:text-neutral-400 disabled:bg-neutral-50/50 dark:disabled:bg-neutral-900/50 disabled:border-neutral-200 dark:disabled:border-neutral-700 disabled:cursor-default"
                  onClick={() => setShowArchiveDialog(true)}
                  disabled={isArchiving}
                >
                  <TrashIcon className="size-3.5" />
                  Archive
                  {isArchiving && <Spinner size="size-3.5" color="neutral" />}
                </Headless.Button>
              </motion.div>
            )}
          </AnimatePresence>

          {!calls.length ? (
            <div className="text-center py-12">
              <PhoneIcon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
              <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
                No calls found
              </h3>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                {activeFilters.length > 0
                  ? 'No calls match your filters. Try adjusting your criteria.'
                  : "Your clients haven't made any calls to Cara yet."}
              </p>
            </div>
          ) : (
            <>
              <DataTable
                data={calls}
                columns={columns}
                keyExtractor={(c) => c.id}
                rowHref={(c) => `/app/calls/log/${c.id}`}
                tableClassName="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSort={(key, dir) => {
                  setSortKey(key);
                  setSortDirection(dir);
                }}
                loading={searchLoading}
              />
              {(hasNextPage || totalCount > calls.length) && (
                <div className="px-4 py-1.5 text-center">
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">
                    Showing {calls.length} of {totalCount} calls.
                    {hasNextPage && (
                      <>
                        {' '}
                        <Headless.Button
                          type="button"
                          onClick={loadMore}
                          disabled={searchLoading}
                          aria-busy={searchLoading}
                          className="inline text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:underline font-medium disabled:text-neutral-400 dark:disabled:text-neutral-500 disabled:no-underline"
                        >
                          {searchLoading ? 'Loading…' : 'Load more'}
                        </Headless.Button>
                      </>
                    )}
                    {searchError && calls.length > 0 && (
                      <span className="ml-2 text-rose-600 dark:text-rose-400">
                        Unable to load more calls.
                      </span>
                    )}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <Dialog open={showArchiveDialog} onClose={() => setShowArchiveDialog(false)} size="md">
        <DialogTitle>Archive Calls</DialogTitle>
        <DialogDescription>
          Are you sure you want to archive <strong>{selectedCalls.length}</strong>{' '}
          {selectedCalls.length === 1 ? 'call' : 'calls'}? This can&apos;t be undone.
        </DialogDescription>
        <DialogActions>
          <Button
            type="button"
            color="white"
            onClick={() => setShowArchiveDialog(false)}
            disabled={isArchiving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            color="sky"
            onClick={() => {
              submit(
                { callIds: selectedCalls },
                { method: 'POST', action: '/app/calls/log/archive', encType: 'application/json' }
              );
              setShowArchiveDialog(false);
            }}
            loading={isArchiving}
          >
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      <AnimatePresence mode="wait">{outlet && React.cloneElement(outlet, {})}</AnimatePresence>
    </>
  );
};
export const CallPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const submit = useSubmit();
  const navigation = useNavigation();

  const loader = useLoaderData() as
    | {
        data: Promise<{
          call: CallFieldsFragment & CallTranscriptFieldsFragment;
        }>;
      }
    | undefined;

  const onClose = () => {
    if (location.key === 'default') {
      navigate('/app/calls/log');
    } else {
      navigate(-1);
    }
  };

  const canUpdate = usePermissions().hasAllPermissions(UPDATE_CALL);

  const toggleSeen = (callId: string, markSeen: boolean) => {
    submit(
      { callIds: [callId], markSeen },
      { method: 'POST', action: '/app/calls/log/mark-seen', encType: 'application/json' }
    );
  };

  const isTogglingSeenState =
    navigation.state === 'submitting' && navigation.formAction?.endsWith('/mark-seen');

  return (
    <MountableSlideout
      onClose={onClose}
      title="Call Summary"
      actions={
        <React.Suspense fallback={null}>
          <Await resolve={loader?.data}>
            {(data) =>
              data?.call &&
              canUpdate && (
                <MarkCallSeenButton
                  seenAt={data.call.seenAt}
                  disabled={isTogglingSeenState}
                  onToggle={(markSeen) => toggleSeen(data.call.id, markSeen)}
                />
              )
            }
          </Await>
        </React.Suspense>
      }
    >
      <React.Suspense fallback={<></>}>
        <Await resolve={loader?.data}>
          {(data) => data?.call && <ConnectedCallDetail call={data.call} />}
        </Await>
      </React.Suspense>
    </MountableSlideout>
  );
};
